import { Component, type ReactNode } from 'react';

/**
 * The last line of defence against a white screen.
 *
 * A single component that throws during render takes the entire React tree with
 * it — and until 2026-09-03 the dashboard had exactly one boundary, around the
 * map, so any other throw blanked the whole app (staging then production: the
 * evidence inbox hit a reason-for-access race and the page went white). This is
 * the systemic answer: a crash shows a card the operator can act on, and — when
 * it wraps the screen area rather than the root — the navigation stays put so
 * they can move somewhere that works.
 *
 * `resetKey` lets a parent clear the failed state on navigation: pass the
 * current screen id, and changing screens re-mounts the subtree fresh rather
 * than stranding the operator on the error card after they have navigated away.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; resetKey?: string; fallback?: (reset: () => void) => ReactNode },
  { failed: boolean; message: string }
> {
  override state = { failed: false, message: '' };

  static getDerivedStateFromError(error: unknown): { failed: boolean; message: string } {
    return { failed: true, message: error instanceof Error ? error.message : String(error) };
  }

  override componentDidUpdate(prev: { resetKey?: string }): void {
    if (prev.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false, message: '' });
    }
  }

  override componentDidCatch(error: unknown): void {
    // Surfaced to the console for the operator's DevTools and any error
    // reporter; not swallowed silently.
    console.error('dashboard error boundary caught:', error);
  }

  private reset = (): void => this.setState({ failed: false, message: '' });

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);
    return (
      <div
        role="alert"
        style={{
          maxWidth: '34rem',
          margin: '4rem auto',
          padding: '1.5rem 1.75rem',
          border: '1px solid var(--line, #e2e2e2)',
          borderRadius: '12px',
          background: 'var(--surface, #fff)',
          color: 'var(--ink, #111)',
          font: '400 15px/1.5 var(--font-sans, system-ui, sans-serif)',
        }}
      >
        <h2 style={{ margin: '0 0 0.5rem', font: '600 18px/1.3 var(--font-sans, system-ui)' }}>
          This screen hit a problem
        </h2>
        <p style={{ margin: '0 0 1rem', color: 'var(--ink-2, #555)' }}>
          Nothing has been lost — your data is held by the service, not this page. Try again, or
          move to another screen from the menu.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            type="button"
            onClick={this.reset}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--accent, #1a1a1a)',
              color: 'var(--on-accent, #fff)',
              font: '500 14px/1 var(--font-sans, system-ui)',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: '1px solid var(--line, #ddd)',
              background: 'transparent',
              color: 'var(--ink, #111)',
              font: '500 14px/1 var(--font-sans, system-ui)',
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
        {this.state.message ? (
          <p
            style={{
              margin: '1rem 0 0',
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              background: 'var(--surface-2, #f5f5f5)',
              color: 'var(--ink-3, #777)',
              font: '400 12px/1.4 var(--font-mono, ui-monospace, monospace)',
              wordBreak: 'break-word',
            }}
          >
            {this.state.message}
          </p>
        ) : null}
      </div>
    );
  }
}
